import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Bell, ArrowsClockwise, Trash, PaperPlaneTilt } from "@phosphor-icons/react";

export default function AdminWaitlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/waitlist").then(r => setRows(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this waitlist entry?")) return;
    await api.delete(`/admin/waitlist/${id}`);
    load();
  };
  const notifyNext = async (instanceId) => {
    if (!window.confirm("Notify the next person on the waitlist for this date? They get a 30-min first-dibs lock.")) return;
    const r = await api.post(`/admin/waitlist/${instanceId}/notify-next`);
    if (r.data.notified) {
      alert("Next person notified.");
    } else {
      alert("No eligible waitlist entry or no seats open.");
    }
    load();
  };

  // Group by instance
  const groups = rows.reduce((acc, e) => {
    const key = e.instance_id;
    if (!acc[key]) acc[key] = { instance_id: key, tour_title: e.tour_title, starts_at: e.instance_starts_at, entries: [] };
    acc[key].entries.push(e);
    return acc;
  }, {});

  return (
    <div className="p-10 md:p-12" data-testid="admin-waitlist">
      <p className="label-eyebrow text-ms-stone">Bookings</p>
      <h1 className="serif text-4xl text-ms-earth mt-2 flex items-center gap-3">
        <Bell size={32} weight="duotone" className="text-ms-brass" /> Waitlist
      </h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Guests waiting for a seat on sold-out tour dates. When a booking is refunded the next person is notified automatically with a 30-min first-dibs lock — you can also notify manually below.
      </p>
      <div className="mt-4">
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-ms-edge rounded-md text-ms-stone hover:bg-ms-cream" data-testid="waitlist-refresh">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      <div className="mt-8 space-y-5">
        {loading ? (
          <p className="text-sm text-ms-stone">Loading…</p>
        ) : Object.keys(groups).length === 0 ? (
          <p className="text-sm text-ms-stone bg-white border border-ms-edge rounded-md p-6">No active waitlist entries.</p>
        ) : (
          Object.values(groups).map(g => (
            <section key={g.instance_id} className="bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid={`waitlist-group-${g.instance_id}`}>
              <div className="px-6 py-4 border-b border-ms-edge bg-ms-cream/40 flex items-center justify-between">
                <div>
                  <p className="serif text-lg text-ms-earth">{g.tour_title || "Tour"}</p>
                  <p className="text-xs text-ms-stone">{g.starts_at ? new Date(g.starts_at).toLocaleString() : ""}</p>
                </div>
                <button
                  onClick={() => notifyNext(g.instance_id)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass label-eyebrow"
                  data-testid={`waitlist-notify-next-${g.instance_id}`}
                >
                  <PaperPlaneTilt size={12} /> Notify next
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-white border-b border-ms-edge">
                  <tr>
                    <th className="text-left p-4 label-eyebrow text-[10px]">#</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Name</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Email</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Phone</th>
                    <th className="text-right p-4 label-eyebrow text-[10px]">Party</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Status</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Joined</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.entries.map(e => (
                    <tr key={e.id} className="border-b border-ms-edge last:border-0" data-testid={`waitlist-row-${e.id}`}>
                      <td className="p-4 text-ms-stone">{e.position}</td>
                      <td className="p-4 text-ms-earth">{e.name}</td>
                      <td className="p-4 text-ms-stone">{e.email}</td>
                      <td className="p-4 text-ms-stone">{e.phone || "—"}</td>
                      <td className="p-4 text-right text-ms-earth">{e.party_size}</td>
                      <td className="p-4 text-ms-stone capitalize">{e.status}</td>
                      <td className="p-4 text-xs text-ms-stone">{new Date(e.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => remove(e.id)} className="text-ms-danger hover:text-ms-danger/70" data-testid={`waitlist-remove-${e.id}`}>
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
